/**
 * Importi i formatit të përdoruesit: .docx → rishikim i vendeve bosh → template.
 *
 * Përdoruesi s'duhet ta rishkruajë kontratën e vet me «@FirstName» që ta sjellë
 * në sistem. Ngarkon dokumentin ashtu siç e ka, sistemi i gjen vijat bosh dhe
 * propozon fushën për secilën (utils/templateImport), dhe këtu ai vetëm
 * konfirmon ose ndryshon — një listë, jo një editor.
 */
import React, { useMemo, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Select, Stack, ToggleButton, ToggleButtonGroup,
  Typography, useMediaQuery, useTheme, alpha,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { detectSlots, applySlots } from '../../utils/templateImport';
import { docxToHtml } from '../../utils/docxImport';
import { CONTRACT_TEMPLATE_TAGS } from '../../config/contractTemplateTags';

const TAG_LABEL = Object.fromEntries(CONTRACT_TEMPLATE_TAGS.map((t) => [t.key, t.label]));

// Emrat e shkurtër për propozimet e përbëra — «Kompania – Emri, me seli në
// Kompania – Adresa» lexohej si zhurmë; brenda një fjalie konteksti mjafton.
const SHORT_LABEL = {
  ...TAG_LABEL,
  FullName: 'Emri e mbiemri',
  CompanyName: 'Emri i kompanisë',
  CompanyAddress: 'Adresa e kompanisë',
  CompanyNUI: 'NUI',
  CompanyDirector: 'Përfaqësuesi i kompanisë',
  PersonalId: 'Numri personal',
};
const tokens = (value) => String(value || '').replace(/@FirstName @LastName/g, '@FullName').split(/(@\w+)/).filter(Boolean);

/** «@FirstName @LastName, nr. personal @PersonalId» → «Emri e mbiemri, nr. personal Numri personal». */
const readable = (value) => tokens(value).map((t) => (t[0] === '@' ? SHORT_LABEL[t.slice(1)] || t : t)).join('');

/** E njëjta, me fushat të theksuara — ku lexohet vendi i plotësuar. */
function FieldText({ value }) {
  return tokens(value).map((t, i) => (t[0] === '@'
    ? <Box key={i} component="span" sx={{ fontWeight: 700 }}>{SHORT_LABEL[t.slice(1)] || t}</Box>
    : <React.Fragment key={i}>{t}</React.Fragment>));
}

function ReviewDialog({ open, html, fileName, onCancel, onApply }) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const { html: base, slots } = useMemo(() => detectSlots(html || ''), [html]);
  const [choices, setChoices] = useState(() => Object.fromEntries(slots.map((s) => [s.id, s.suggestion])));
  const [stripHints, setStripHints] = useState(true);
  const [view, setView] = useState('all');

  const linked = slots.filter((s) => choices[s.id]).length;
  const explicit = (base.match(/@\w+/g) || []).length;
  const shown = view === 'empty' ? slots.filter((s) => !choices[s.id]) : slots;

  const apply = () => {
    const out = applySlots(base, slots, choices, { stripHints });
    onApply(out, { fields: (out.match(/@\w+/g) || []).length, blanks: slots.length - linked });
  };

  return (
    <Dialog open={open} onClose={onCancel} fullScreen={phone} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography component="div" sx={{ fontWeight: 700, fontSize: 18 }}>Kontrollo fushat e dokumentit</Typography>
        <Typography component="div" variant="body2" color="text.secondary" noWrap>
          {fileName}
        </Typography>
      </DialogTitle>

      <Box sx={{ px: 3, pb: 1.5 }}>
        {slots.length > 0 ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={`${slots.length} vende për t'u plotësuar`} />
              <Chip size="small" color="success" variant="outlined" icon={<AutoFixHighIcon />} label={`${linked} të lidhura`} />
              <Chip size="small" variant="outlined" label={`${slots.length - linked} për plotësim me dorë`} />
            </Stack>
            <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
              <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.5 }}>Të gjitha</ToggleButton>
              <ToggleButton value="empty" sx={{ textTransform: 'none', px: 1.5 }}>Pa fushë</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Dokumenti nuk ka vija bosh (____){explicit ? `, por u njohën ${explicit} fusha të shënuara si [Emri]` : ''}.
            Importohet ashtu siç është — fushat e tjera i shtoni në editor me një klik.
          </Typography>
        )}
      </Box>

      {slots.length > 0 && (
        <DialogContent dividers sx={{ p: 0 }}>
          {shown.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>Çdo vend bosh ka një fushë.</Typography>
          )}
          {shown.map((s) => {
            const value = choices[s.id] || '';
            const isSuggested = value && value === s.suggestion;
            return (
              <Stack
                key={s.id}
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 2 }}
                alignItems={{ sm: 'center' }}
                sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, lineHeight: 1.5 }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>{s.label ? `…${s.label} ` : ''}</Box>
                    <Box component="span" sx={{
                      px: 0.75, py: 0.125, borderRadius: 1, boxDecorationBreak: 'clone', WebkitBoxDecorationBreak: 'clone',
                      bgcolor: (t) => (value ? alpha(t.palette.success.main, 0.12) : t.palette.action.selected),
                      color: value ? 'success.dark' : 'text.secondary',
                    }}>
                      {value ? <FieldText value={value} /> : (s.placeholder || '______')}
                    </Box>
                  </Typography>
                  {s.hint && (
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic', mt: 0.25 }}>
                      ({s.hint})
                    </Typography>
                  )}
                </Box>
                <Select
                  size="small"
                  value={value}
                  displayEmpty
                  onChange={(e) => setChoices((c) => ({ ...c, [s.id]: e.target.value }))}
                  sx={{ width: { xs: '100%', sm: 280 }, flexShrink: 0, fontSize: 13.5 }}
                  renderValue={(v) => (v ? readable(v) : <Box component="span" sx={{ color: 'text.secondary' }}>Lëre bosh</Box>)}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                >
                  <MenuItem value=""><em>Lëre bosh — plotësohet me dorë</em></MenuItem>
                  {s.suggestion && s.suggestion.includes(',') && (
                    <MenuItem value={s.suggestion} sx={{ whiteSpace: 'normal' }}>
                      <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'success.main' }}>PROPOZIM</Typography>
                        <Typography sx={{ fontSize: 13.5 }}>{readable(s.suggestion)}</Typography>
                      </Box>
                    </MenuItem>
                  )}
                  {CONTRACT_TEMPLATE_TAGS.filter((t) => t.key !== 'CompanyLogo').map((t) => (
                    <MenuItem key={t.key} value={`@${t.key}`}>{t.label}</MenuItem>
                  ))}
                </Select>
                {!phone && (
                  <Box sx={{ width: 56, flexShrink: 0, textAlign: 'right' }}>
                    {isSuggested && <Chip size="small" label="auto" color="success" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
                    {!value && s.reason === 'manual' && (
                      <Chip size="small" label="dorë" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                    )}
                    {!value && s.reason === 'unknown' && (
                      <Chip size="small" label="zgjidh" color="warning" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                    )}
                  </Box>
                )}
              </Stack>
            );
          })}
        </DialogContent>
      )}

      <DialogActions sx={{ px: 3, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
        {slots.length > 0 && (
          <FormControlLabel
            sx={{ mr: 'auto' }}
            control={<Checkbox size="small" checked={stripHints} onChange={(e) => setStripHints(e.target.checked)} />}
            label={<Typography variant="body2">Hiq udhëzimet në kllapa te vendet e plotësuara</Typography>}
          />
        )}
        <Button onClick={onCancel} sx={{ textTransform: 'none' }}>Anulo</Button>
        <Button variant="contained" disableElevation onClick={apply} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Importo dokumentin
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Gjithë rrjedha e importit për një faqe: `start(file)` e lexon dokumentin dhe
 * hap rishikimin; `onApplied(html, stats, fileName)` merr template-in gati.
 * `dialog` vendoset diku në JSX të faqes.
 */
export function useTemplateImport({ onApplied, onError }) {
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const start = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const html = await docxToHtml(file);
      setPending({ html, fileName: file.name, key: Date.now() });
    } catch (e) {
      onError?.(e?.message || 'Nuk u arrit importi nga Word.');
    } finally {
      setBusy(false);
    }
  };

  const dialog = pending ? (
    <ReviewDialog
      key={pending.key}
      open
      html={pending.html}
      fileName={pending.fileName}
      onCancel={() => setPending(null)}
      onApply={(out, stats) => {
        const { fileName } = pending;
        setPending(null);
        onApplied(out, stats, fileName);
      }}
    />
  ) : null;

  return { start, busy, dialog };
}

export default ReviewDialog;
